import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { XCircle, AlertTriangle, ArrowRight, CheckCircle2, ArrowUpRight } from 'lucide-react';

const QUESTIONS = [
  {
    n: 1,
    q: 'Can we reconstruct any transaction end-to-end without a human digging through three systems?',
    grade_before: 'fail',
    grade: 'pass_n1',
    answer: 'Yes — for the one real transaction we currently have. A multi-network indexer polls Base mainnet and Base Sepolia every 30 seconds, decodes every KojiruACSHook event, and stores them in a single MongoDB collection joined by agent_id. GET /api/indexer/events/{agent_id} returns a full ordered timeline. The one real Sepolia slash (block 43,445,800, tx 0x694ffe5f…f1d276) is fully reconstructible from the DB alone, with no additional system lookups.',
    since_aug25: 'Multi-network indexer + packet producer shipped. Verified reconstruction against a real historical Sepolia slash.',
    still_not: 'Proven at n=1 across one event type. Not stress-tested under chain reorgs, RPC failover, adversarial event ordering, or high event throughput. If interpreted as "reliably at production scale," this remains open.',
  },
  {
    n: 2,
    q: 'What percentage of our refunds and exceptions touch a person?',
    grade_before: 'fail',
    grade: 'fail',
    answer: 'Unknown. We have no denominator. Zero real refunds and zero real exceptions have been processed on the platform. The design intent is that on-chain slashes settle atomically without human involvement, but that is design intent — not a measurement.',
    since_aug25: 'No change. Shipping code cannot produce a denominator; only real counterparty volume can.',
    still_not: 'A verified percentage from real production volume. This question closes when live agents, live counterparties, and live exceptions produce a reportable rate.',
  },
  {
    n: 3,
    q: 'Could we produce complete dispute evidence programmatically, today?',
    grade_before: 'partial',
    grade: 'partial_upgraded',
    answer: 'Partially. A Packet Producer service now fires on every indexed Slashed event, joins it with the agent\'s full on-chain history, and writes a signed JSON packet to /proofs. slash-sepolia-000001.json is the first real one. The packet carries: tx hash, block, log_index, agent registration, collateral history, harm accounting (debt in cents + slash amount in KOJI), state changes before/after, and an EIP-191 content hash signature.',
    since_aug25: 'Packet Producer shipped and proven on a real Sepolia slash. On-chain evidence is now programmatic.',
    still_not: 'The packet does not include off-chain context — the harmed party\'s original claim, the delivery evidence that failed, the chain-of-custody timestamps for when a counterparty first reported the breach. A payments-veteran definition of "complete dispute evidence" would include both halves. We ship the on-chain half. The off-chain-linkage half is unbuilt.',
  },
  {
    n: 4,
    q: 'Does settlement reconcile without manual adjustments we have learned to accept?',
    grade_before: 'fail',
    grade: 'fail',
    answer: 'Unknown at any meaningful scale. Exactly one real Sepolia settlement has occurred (June 28, 2026). It settled atomically in one block, no manual adjustment was needed, no counterparty contested it. That is n=1 on a testnet. It is not a reconciliation posture.',
    since_aug25: 'Progressed from "never fired" to "fired once cleanly." The indexer\'s state confirms the packet matches the on-chain event exactly, with zero reconciliation gap.',
    still_not: 'A body of settlements — uncontested + contested + edge-case — with an auditable "zero manual adjustments" report attached. Not proven at any scale. Not tested under adversarial conditions. This question closes when we have that report, not before. A mainnet "rehearsal slash" is technically ready to fire (see /app/backend/scripts/base_mainnet_slash_smoke_test.py) and was intentionally deferred: firing at n=1 without a real breach would move the count to n=2 but would not close the question — and it would invite the criticism of self-manufactured evidence. The first mainnet slash will be a real one, or one requested by a specific institutional counterparty as a due-diligence witness.',
  },
  {
    n: 5,
    q: 'If an autonomous agent initiated 10 percent of our volume next year, which team breaks first?',
    grade_before: 'fail',
    grade: 'fail',
    answer: 'All of them. Kojiru is a founder-led operation today. There is no on-call rotation. No monitored customer-support channel outside working hours. No dedicated operations owner. If agent volume spiked ten-fold tomorrow, the human bottleneck (one person) is what breaks — not the code.',
    since_aug25: 'The /ops playbook is published. A live /status page auto-refreshes with per-network indexer sync, event counts, packet count, and real recourse-paid USD. Observability improved. Human capacity did not.',
    still_not: 'A dedicated ops owner. An on-call rotation. A monitored channel with a written SLA. This gap closes with a hire or an operations vendor, not with more engineering.',
  },
];

const GRADE_MAP = {
  fail: { icon: XCircle, cls: 'text-rose-400 border-rose-500/40 bg-rose-500/10', label: 'STILL FAIL', border: 'border-rose-500/50' },
  partial: { icon: AlertTriangle, cls: 'text-amber-400 border-amber-500/40 bg-amber-500/10', label: 'PARTIAL', border: 'border-amber-500/50' },
  partial_upgraded: { icon: ArrowUpRight, cls: 'text-amber-300 border-amber-400/50 bg-amber-500/15', label: 'PARTIAL · UPGRADED', border: 'border-amber-400/60' },
  pass_n1: { icon: CheckCircle2, cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', label: 'PASS AT n=1', border: 'border-emerald-500/50' },
  pass: { icon: CheckCircle2, cls: 'text-emerald-400 border-emerald-500/50 bg-emerald-500/15', label: 'PASS', border: 'border-emerald-500/60' },
};

const Grade = ({ g }) => {
  const { icon: Icon, cls, label } = GRADE_MAP[g];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-bold tracking-[1.2px] ${cls}`}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
};

const BeforeGrade = ({ g }) => {
  const { label } = GRADE_MAP[g];
  return (
    <span className="text-[10px] font-bold tracking-[1.2px] text-slate-500 line-through">
      was: {label.replace(' · UPGRADED', '').replace('STILL ', '').replace(' AT n=1', '')}
    </span>
  );
};

export default function Readiness() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Readiness · Kojiru</title>
        <meta name="description" content="Public re-audit against a senior payments-industry analyst's five-question agentic-commerce readiness test. Six months on: one passed at n=1, one partial upgraded, three still open. Two of the three cannot be closed by code alone." />
      </Helmet>
      <Navbar />

      <section className="pt-32 pb-14 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-rose-400 mb-6" data-testid="readiness-eyebrow">
              PUBLIC SELF-AUDIT &middot; RE-AUDITED 19 FEB 2026
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              One passed. One upgraded. Three still open.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
              On August 25, 2026, a senior payments-industry analyst (25+ years) published five questions every payment platform should
              answer before touching agentic commerce. We ran Kojiru against them the same day. We failed
              four and a half of five, and canceled a meeting we were not ready for.
            </p>
            <p className="text-slate-400 leading-relaxed max-w-3xl mb-4">
              Six months later we re-audited ourselves against the same five questions. This is the honest
              result. Question 1 flipped from Fail to Pass — but only at n=1, on one Sepolia slash. Question 3
              moved from Partial to Partial-Upgraded — we ship the on-chain half of dispute evidence but not
              the off-chain-linkage half. Questions 2, 4, and 5 have not moved. Below, each question shows
              what it was on 25 August, what we shipped since, and precisely what it would still take to close.
            </p>
            <p className="text-slate-400 leading-relaxed max-w-3xl italic">
              Two of the still-open questions cannot be closed by shipping more code. Question 2 needs real
              counterparty volume &mdash; a denominator. Question 5 needs a hire, or a monitoring vendor with a
              written SLA. We say this so no reader has to guess.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          {QUESTIONS.map((q) => {
            const border = GRADE_MAP[q.grade].border;
            return (
              <motion.div
                key={q.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`border-l-2 pl-6 sm:pl-8 ${border}`}
                data-testid={`readiness-q${q.n}`}
              >
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <div className="text-xs font-bold tracking-[2px] text-slate-500">QUESTION {q.n}</div>
                  <Grade g={q.grade} />
                  {q.grade !== q.grade_before && <BeforeGrade g={q.grade_before} />}
                </div>
                <h3 className="text-xl sm:text-2xl font-black mb-4 leading-tight">{q.q}</h3>
                <p className="text-slate-300 leading-relaxed mb-4">{q.answer}</p>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex gap-3">
                    <div className="text-[10px] font-bold tracking-[1.2px] text-emerald-300 flex-shrink-0 w-32 pt-0.5">SINCE 25 AUG</div>
                    <div className="text-slate-300 leading-relaxed">{q.since_aug25}</div>
                  </div>
                  <div className="flex gap-3">
                    <div className="text-[10px] font-bold tracking-[1.2px] text-rose-300 flex-shrink-0 w-32 pt-0.5">STILL NOT</div>
                    <div className="text-slate-400 leading-relaxed">{q.still_not}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">WHAT IT WILL TAKE TO CLOSE THE REMAINING GAPS</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            Three questions still open. Two need something other than code.
          </h2>
          <ol className="space-y-5 text-slate-300 leading-relaxed">
            <li className="flex gap-4">
              <span className="text-rose-400 font-bold w-8 flex-shrink-0">Q2</span>
              <div>
                <strong className="text-white">Real counterparty volume.</strong> This question closes when
                live agents and live counterparties produce a real refund/exception rate we can measure.
                Not a code problem. A distribution problem.
              </div>
            </li>
            <li className="flex gap-4">
              <span className="text-amber-400 font-bold w-8 flex-shrink-0">Q3</span>
              <div>
                <strong className="text-white">Off-chain evidence linkage.</strong> On-chain evidence is
                shipped. The remaining half is a submission channel for the harmed party&rsquo;s original
                claim + delivery-failure evidence, cryptographically bound to the on-chain event. Probably
                an EIP-712 dispute-attestation format. Design work, then implementation.
              </div>
            </li>
            <li className="flex gap-4">
              <span className="text-rose-400 font-bold w-8 flex-shrink-0">Q4</span>
              <div>
                <strong className="text-white">A body of settlements to reconcile.</strong> n=1 is not
                reconciliation. This closes with enough real settlements &mdash; uncontested, contested,
                edge-case &mdash; that an accounting report can honestly say &ldquo;zero manual
                adjustments across N settlements.&rdquo; Blocked on the same distribution problem as Q2.
              </div>
            </li>
            <li className="flex gap-4">
              <span className="text-rose-400 font-bold w-8 flex-shrink-0">Q5</span>
              <div>
                <strong className="text-white">A hire, or a monitoring vendor with a written SLA.</strong>{' '}
                The <Link to="/ops" className="text-cyan-300 hover:underline">/ops playbook</Link> is
                published and the <Link to="/status" className="text-cyan-300 hover:underline">/status page</Link>{' '}
                is live &mdash; observability improved. But no on-call rotation exists. Closing this
                question is a budget decision, not an engineering decision.
              </div>
            </li>
          </ol>
          <p className="text-slate-400 leading-relaxed italic mt-8">
            No dates. Every gap closes when it closes, publicly, on this page. When a question moves, we
            will say so &mdash; and show the evidence. We would rather this page overstate what is open
            than what is closed.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-emerald-400 mb-3">WHAT MOVED SINCE 25 AUGUST</div>
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
            Concrete artifacts you can inspect.
          </h2>
          <ul className="space-y-3 text-slate-300 leading-relaxed text-sm">
            <li className="flex gap-3">
              <span className="text-emerald-400 flex-shrink-0">&rarr;</span>
              <div>
                <strong className="text-white">Multi-network on-chain indexer</strong> &mdash; polls Base
                mainnet + Base Sepolia every 30 seconds. Public health at{' '}
                <Link to="/status" className="text-cyan-300 hover:underline">/status</Link>.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-emerald-400 flex-shrink-0">&rarr;</span>
              <div>
                <strong className="text-white">Packet Producer</strong> &mdash; fires on every indexed
                Slashed event, writes a signed JSON packet to{' '}
                <Link to="/proofs" className="text-cyan-300 hover:underline">/proofs</Link>. First real
                packet: <code className="text-slate-200 text-xs">slash-sepolia-000001.json</code>.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-emerald-400 flex-shrink-0">&rarr;</span>
              <div>
                <strong className="text-white">KYPA Credential Envelope v1.0</strong> &mdash; the &ldquo;instrument&rdquo; per the analyst&rsquo;s comment
                on a former global-wallet product leader&rsquo;s trust-chain post. Live spec + interactive sandbox at{' '}
                <Link to="/kypa/instrument" className="text-cyan-300 hover:underline">/kypa/instrument</Link>.
                (This closes an <em>adjacent</em> gap &mdash; agents cannot transact without a mintable
                credential &mdash; not one of the analyst&rsquo;s five.)
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-emerald-400 flex-shrink-0">&rarr;</span>
              <div>
                <strong className="text-white">/ops and /status pages</strong> &mdash; operational
                transparency artifacts. Observability changes, not organizational changes.
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section className="py-14 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 max-w-2xl mx-auto mb-6 leading-relaxed">
            The questions above were written by a senior payments-industry analyst (25+ years){' '}
            in his <em>Inside Payments</em> newsletter on 25 August 2026. This page is our public
            re-audit against those questions, updated 19 February 2026. If any answer overreaches or
            understates &mdash; especially any grade that reads more generous than the underlying
            evidence &mdash; tell us: <a href="mailto:wayne@kojiru.com" className="text-cyan-300 hover:underline">wayne@kojiru.com</a>. We will correct it before anyone else reads.
          </p>
          <Link to="/ops"><Button size="lg" className="bg-white text-black hover:bg-slate-100" data-testid="readiness-to-ops">See the operations playbook <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
